import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, AlertTriangle, MessageSquare, CheckCircle, XCircle, Clock } from "lucide-react";
import toast from "react-hot-toast";
import {
  fetchWarningsByEmail,
  fetchJustificationsByWarningId,
  createJustification,
  updateJustificationStatus,
  updateJustification,
} from "../../redux/warningSlice";
import JustificationModal from "./JustificationModal";

const WarningModal = ({ isOpen, onClose, userEmail, isAdmin = false }) => {
  const dispatch = useDispatch();
  const { userWarnings, warningJustifications, status } = useSelector((state) => state.warnings);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [isJustificationModalOpen, setIsJustificationModalOpen] = useState(false);
  const [isEditJustification, setIsEditJustification] = useState(false);
  const [currentJustification, setCurrentJustification] = useState(null);

  useEffect(() => {
    if (isOpen && userEmail) {
      dispatch(fetchWarningsByEmail(userEmail));
    }
  }, [isOpen, userEmail, dispatch]);

  const handleJustifyWarning = (warning) => {
    setSelectedWarning(warning);
    setIsEditJustification(false);
    setIsJustificationModalOpen(true);
    // Fetch existing justifications for this warning
    dispatch(fetchJustificationsByWarningId(warning.id));
  };

  const handleEditJustification = (justification) => {
    setCurrentJustification(justification);
    setIsEditJustification(true);
    setIsJustificationModalOpen(true);
  };

  const handleJustificationSubmit = async (data) => {
    try {
      if (isEditJustification && currentJustification) {
        // Update existing justification
        await dispatch(updateJustification({
          warningId: selectedWarning.id,
          id: currentJustification.id,
          justification: data.justificationText
        })).unwrap();
        toast.success("Justification updated successfully!");
      } else {
        // Create new justification
        await dispatch(createJustification({
          warningId: selectedWarning.id,
          email: userEmail,
          justificationText: data.justificationText
        })).unwrap();
        toast.success("Justification submitted successfully!");
      }
      setIsJustificationModalOpen(false);
      setSelectedWarning(null);
      setCurrentJustification(null);
      // Refresh justifications
      if (selectedWarning) {
        dispatch(fetchJustificationsByWarningId(selectedWarning.id));
      }
    } catch (error) {
      toast.error(error.message || "Failed to submit justification");
    }
  };

  const handleApproveJustification = async (justificationId) => {
    try {
      await dispatch(updateJustificationStatus({
        id: justificationId,
        status: "Approved"
      })).unwrap();
      toast.success("Justification approved!");
      if (selectedWarning) {
        dispatch(fetchJustificationsByWarningId(selectedWarning.id));
      }
    } catch (error) {
      toast.error(error.message || "Failed to approve justification");
    }
  };

  const handleRejectJustification = async (justificationId) => {
    try {
      await dispatch(updateJustificationStatus({
        id: justificationId,
        status: "Rejected"
      })).unwrap();
      toast.success("Justification rejected!");
      if (selectedWarning) {
        dispatch(fetchJustificationsByWarningId(selectedWarning.id));
      }
    } catch (error) {
      toast.error(error.message || "Failed to reject justification");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "Rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-11/12 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <AlertTriangle className="w-5 h-5 text-orange-500 mr-2" />
            {isAdmin ? "All Warnings" : "My Warnings"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {status === "loading" ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading warnings...</p>
          </div>
        ) : userWarnings.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No warnings found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {userWarnings.map((warning) => (
              <div key={warning.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800">
                      {warning.subject}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Date: {new Date(warning.warningDate).toLocaleDateString()}
                    </p>
                  </div>
                  {!isAdmin && (
                    <button
                      onClick={() => handleJustifyWarning(warning)}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 flex items-center"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Justify
                    </button>
                  )}
                </div>
                
                <p className="text-gray-700 mb-3">{warning.description}</p>
                
                {/* Justifications Section */}
                <div className="mt-4">
                  <h4 className="font-medium text-gray-800 mb-2">Justifications:</h4>
                  {warningJustifications
                    .filter(j => j.warningId === warning.id)
                    .map((justification) => (
                      <div key={justification.id} className="bg-gray-50 p-3 rounded mb-2">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(justification.status)}`}>
                              {getStatusIcon(justification.status)}
                              <span className="ml-1">{justification.status}</span>
                            </span>
                          </div>
                          {isAdmin && justification.status === "Pending" && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleApproveJustification(justification.id)}
                                className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectJustification(justification.id)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          {!isAdmin && justification.status === "Pending" && (
                            <button
                              onClick={() => handleEditJustification(justification)}
                              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm">{justification.justificationText}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Submitted: {new Date(justification.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  
                  {warningJustifications.filter(j => j.warningId === warning.id).length === 0 && (
                    <p className="text-gray-500 text-sm italic">No justifications submitted yet</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Justification Modal */}
        <JustificationModal
          isOpen={isJustificationModalOpen}
          onClose={() => {
            setIsJustificationModalOpen(false);
            setSelectedWarning(null);
            setCurrentJustification(null);
          }}
          onSubmit={handleJustificationSubmit}
          isEdit={isEditJustification}
          initialData={currentJustification}
        />
      </div>
    </div>
  );
};

export default WarningModal;